import { useMe, type Me, type Permission, type Pharmacy } from '@care-platform/api-client';
import { useAuth } from '@care-platform/auth';
import { Button, ErrorState, LoadingState } from '@care-platform/ui';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useCopy } from './copy';

export interface Session {
  user: Me['user'];
  role: Me['role'];
  /** The single active pharmacy from the token (W1). There is no chooser or switcher in SOW-01. */
  pharmacy: Pharmacy;
  permissions: readonly Permission[];
  can: (permission: Permission) => boolean;
}

const SessionContext = createContext<Session | undefined>(undefined);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession must be used inside SessionProvider.');
  return session;
}

/** The active pharmacy context. Every Web MMS result is scoped to it by the BFF; screens show it, never send it. */
export function useActivePharmacy(): Pharmacy {
  return useSession().pharmacy;
}

export function toSession(me: Me): Session {
  const permissions = me.permissions;
  return {
    user: me.user,
    role: me.role,
    pharmacy: me.pharmacy,
    permissions,
    can: (permission) => permissions.includes(permission),
  };
}

/** Lets tests and stories supply a session without the API. */
export function StaticSessionProvider({ session, children }: { session: Session; children: ReactNode }) {
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

/**
 * Resolves the signed-in user's role, active pharmacy and permissions from `GET /me` before rendering anything
 * that needs them.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const copy = useCopy();
  const auth = useAuth();
  const me = useMe();
  const session = useMemo(() => (me.data ? toSession(me.data) : undefined), [me.data]);

  if (me.isPending) return <LoadingState label={copy.shell.loadingSession} className="m-6" />;
  if (!session) {
    return (
      <div className="mx-auto mt-16 max-w-lg space-y-3 px-4">
        <ErrorState
          title={copy.shell.sessionErrorTitle}
          description={copy.shell.sessionErrorBody}
          retryLabel={copy.common.retry}
          onRetry={() => void me.refetch()}
        />
        <Button variant="outline" onClick={() => void auth.signOut()}>
          {copy.shell.signOut}
        </Button>
      </div>
    );
  }
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}
